import { BadgeDelta } from "@tremor/react";
import {
  costInWindow,
  mediaInWindow,
  incarcareInWindow,
  deriveProfitabilitate,
  deriveExportSplit,
} from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { eur, formatLunaKey, procent } from "@/lib/format";
import { KpiCard } from "@/components/ui/KpiCard";

function ultimul<T>(arr: T[]): T | undefined {
  return arr.length ? arr[arr.length - 1] : undefined;
}

export function OverviewKpis() {
  useFilterVersion();
  const cost = costInWindow();
  const mediaMobila = mediaInWindow();
  const masini = incarcareInWindow();
  const categoriiProfitabilitate = deriveProfitabilitate("categorie_produs");
  const exportSplit = deriveExportSplit();

  const ultimulCost = ultimul(cost);
  const ultimaMedie = ultimul(mediaMobila);

  const venitTotal = categoriiProfitabilitate.reduce((s, r) => s + r.venit_eur, 0);
  const marjaTotal = categoriiProfitabilitate.reduce((s, r) => s + r.marja_eur, 0);
  const marjaPctTotal = venitTotal > 0 ? (100 * marjaTotal) / venitTotal : 0;

  const ultimaLunaMasini = masini.length ? masini[masini.length - 1].luna_key : null;
  const masiniLunaCurenta = masini.filter((m) => m.luna_key === ultimaLunaMasini);
  const incarcareMedie =
    masiniLunaCurenta.length > 0
      ? masiniLunaCurenta.reduce((s, m) => s + m.incarcare_pct, 0) / masiniLunaCurenta.length
      : 0;

  const exportRow = exportSplit.find((r) => r.segment === "export");
  const deltaType = ultimaMedie?.semnal_vs_ma6 === "PESTE_MEDIE" ? "increase" : "decrease";

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 sm:gap-4">
      <KpiCard
        accent="blue"
        eticheta={`Cost producție /oră · ${ultimulCost ? formatLunaKey(ultimulCost.luna_key) : "—"}`}
        valoare={ultimulCost ? eur(ultimulCost.cost_pe_ora_eur, 0) : "—"}
        subtext="reper transcript: ~200 €/oră"
        badge={
          ultimaMedie ? (
            <BadgeDelta deltaType={deltaType} size="xs" className="shrink-0">
              {procent(Math.abs(ultimaMedie.deviatie_pct_ma6))} vs MA6
            </BadgeDelta>
          ) : undefined
        }
      />

      <KpiCard
        accent="indigo"
        eticheta="Cost producție /unitate (m²)"
        valoare={ultimulCost ? eur(ultimulCost.cost_pe_unitate_eur, 2) : "—"}
        subtext={ultimulCost ? `${ultimulCost.unitati_m2} m² echiv. produse` : ""}
      />

      <KpiCard
        accent="emerald"
        eticheta="Marjă medie (comenzi filtrate)"
        valoare={procent(marjaPctTotal)}
        subtext={`venit total ${eur(venitTotal, 0)}`}
      />

      <KpiCard
        accent="amber"
        eticheta="Încărcare medie mașini"
        valoare={procent(incarcareMedie)}
        subtext={`${ultimaLunaMasini ? formatLunaKey(ultimaLunaMasini) : ""} · vs. capacitate teoretică`}
      />

      <KpiCard
        accent="rose"
        eticheta="Export % din venit"
        valoare={exportRow ? procent(exportRow.pondere_venit_pct) : "—"}
        subtext="percepție client: 60%"
      />
    </div>
  );
}
